/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import Layout from '@layout/index';
import { Decimal } from '@cosmjs/math';
import { Grid } from '@mui/material/';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import classnames from 'classnames';
import TooltipMUI, { tooltipClasses } from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import QuestionIcon from '@assets/AirQualityPage/question_icon.svg';
import InfoIcon from '@assets/AirQualityPage/info_icon.svg';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import Button, { buttonClasses } from '@mui/material/Button';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import { useStores } from '@stores';
import { BEARS } from '@api';
import BeeSpinnerLoading from '@components/BeeSpinnerLoading';
import { TOOLTIP_DELAY } from '@constants/system';
import { numberWithSeparator, floorRounding } from '@utils';
import styles from './airQualityPage.module.scss';

const StyledButton = styled(Button)(({ theme }) => ({
  [`&.${buttonClasses.root}`]: {
    color: '#4F4F4F',
    backgroundColor: '#FFFFFF',
    '&:active': {
      color: '#FFFFFF',
      backgroundColor: '#2D9CDB',
    },
    '@media (hover: hover)': {
      [`&.${buttonClasses.root}:hover`]: {
        color: '#FFFFFF',
        backgroundColor: '#2D9CDB',
      },
    },
  },
  [`&.${buttonClasses.disabled}`]: {
    color: 'rgba(0, 0, 0, 0.26)',
    backgroundColor: 'rgba(0, 0, 0, 0.12);',
    boxShadow: 'none',
  },
}));

const QualityTooltip = styled(({ className, ...props }) => (
  <TooltipMUI {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: 16,
  },
}));

const AirQualityPage = observer(() => {
  const stores = useStores();

  const [airInfo, setAirInfo] = useState({});
  const [air, setAir] = useState([]);
  const [isAirInfoTableLoading, setAirInfoTableLoading] = useState(true);
  const [offsetAirHistory, setOffsetAirHistory] = useState(0);
  const [isAllHistory, setAllHistory] = useState(true);

  useEffect(() => {
    if (stores.isAnonymousClientConfigured) {
      (async () => {
        try {
          const airInfo = await BEARS.getLastAirInfo();
          let response = await BEARS.getAllAirHistory({ limit: 1 });
          response = await BEARS.getAllAirHistory({ limit: response?.pagination?.total || 50 })
          setAirInfo({
            consume: airInfo?.consume,
            supply: airInfo?.supply,
            count: airInfo?.count,
            purity: airInfo?.purity,
          });
          setAir({
            airHistory: response?.airHistory?.sort((a, b) => a.id - b.id),
            totalAirValues: response?.pagination?.total,
          });
        } catch (err) {
          console.log(err);
        } finally {
          setAirInfoTableLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured, offsetAirHistory]);

  /* ChartJS logic */
  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

  const options = {
    responsive: true,
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Blocks height',
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Air count',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
        position: 'top',
      },
      title: {
        display: true,
        text: 'Air count',
      },
      tooltip: {
        enabled: true,
        position: 'nearest',
        // external: externalTooltipHandler //! externalTooltipHandler
        callbacks: {
          label(tooltipItem) {
            return `Air count: ${floorRounding(tooltipItem.parsed.y)}`;
          },
        },
      },
    },
  };

  const labels = air?.airHistory?.length
    ? air?.airHistory.map((item) => {
        return item.height;
      })
    : [];

  const data = {
    labels,
    datasets: [
      {
        label: 'Air count',
        data: air?.airHistory?.length
          ? air?.airHistory.map((item) => {
              return floorRounding(Decimal.fromAtomics(item.count, 18).toFloatApproximation());
            })
          : [],
        borderColor: '#2D9CDB', // сделать синий как у Тимура 2D9CDB, раньше был FB4517(красный)
      },
    ],
  };

  /* Air Quality Tooltip logic */
  const [openAirQualityTooltip, setOpenAirQualityTooltip] = useState(false);
  const [airQualityTimerId, setAirQualityTimerId] = useState(null);
  const handleAirQualityTooltipClose = () => {
    setOpenAirQualityTooltip(false);
  };
  const handleAirQualityTooltipOpen = () => {
    setOpenAirQualityTooltip(true);
    clearTimeout(airQualityTimerId);
    const currentTimerId = setTimeout(() => handleAirQualityTooltipClose(), TOOLTIP_DELAY);
    setAirQualityTimerId(currentTimerId);
  };

  /* Air Count Tooltip logic */
  const [openAirCountTooltip, setOpenAirCountTooltip] = useState(false);
  const [AirCountTimerId, setAirCountTimerId] = useState(null);
  const handleAirCountTooltipClose = () => {
    setOpenAirCountTooltip(false);
  };
  const handleAirCountTooltipOpen = () => {
    setOpenAirCountTooltip(true);
    clearTimeout(AirCountTimerId);
    const currentTimerId = setTimeout(() => handleAirCountTooltipClose(), TOOLTIP_DELAY);
    setAirCountTimerId(currentTimerId);
  };

  return (
    <Layout>
      <Grid
        container
        sx={{ maxWidth: '1660px', width: '100%' }}
        p={{ xs: '28px 30px 0', sm: '20px 60px 0', lg: '20px 100px 0', xl: '20px 130px 0' }}
      >
        <p className={styles.AirQualityTitle}>Air Quality</p>
        <Grid
          container
          spacing={{ xs: 2, sm: 3, md: 4, lg: 6 }}
          sx={{ width: '100%' }}
          mb={{ xs: '20px', sm: '30px', lg: '50px' }}
        >
          <Grid item xs={12} lg={3}>
            {isAirInfoTableLoading ? (
              <BeeSpinnerLoading
                classNames={{
                  Container: styles.LoadingTablesContainer,
                  Spinner: styles.LoadingTablesSpinner,
                }}
              />
            ) : Object.keys(airInfo).length ? (
              <div className={styles.AirQualityTable}>
                <div className={styles.AirQualityTableTitle}>
                  <div className={styles.AirQualityTableTitleText}>
                    Air Quality
                    <ClickAwayListener onClickAway={handleAirQualityTooltipClose}>
                      <div>
                        <QualityTooltip
                          PopperProps={{
                            disablePortal: true,
                          }}
                          open={openAirQualityTooltip}
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                          title="Air Quality = Air Count * Air Purity"
                        >
                          <img
                            src={QuestionIcon}
                            alt="question_icon"
                            className={styles.QuestionIcon}
                            onClick={() => handleAirQualityTooltipOpen()}
                          />
                        </QualityTooltip>
                      </div>
                    </ClickAwayListener>
                  </div>
                  <div className={styles.AirQualityTableTitleTextBlock}>
                    <p
                      className={classnames(
                        styles.AirQualityTableTitleParameters,
                        styles.AirQualityTableTitleParameters__Color_red,
                      )}
                    >
                      {numberWithSeparator(floorRounding(airInfo?.count, 6))}
                    </p>
                    <p className={styles.AirQualityTableTitleParameters}>
                      &nbsp;/&nbsp;{numberWithSeparator(airInfo?.purity)}
                    </p>
                  </div>
                </div>
                <div className={styles.AirInfoBlock}>
                  <div className={styles.AirInfoBlockTitles}>
                    <p>Air supply</p>
                    <p>Air consume</p>
                    <div className={styles.AirCountTableTitleText}>
                      Air count
                      <ClickAwayListener onClickAway={handleAirCountTooltipClose}>
                        <div>
                          <QualityTooltip
                            PopperProps={{
                              disablePortal: true,
                            }}
                            open={openAirCountTooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            title="Air Count = Air Supply / Air Consume"
                          >
                            <img
                              src={InfoIcon}
                              alt="question_icon"
                              className={styles.InfoIcon}
                              onClick={() => handleAirCountTooltipOpen()}
                            />
                          </QualityTooltip>
                        </div>
                      </ClickAwayListener>
                    </div>
                    <p>Air purity</p>
                  </div>
                  <div className={styles.AirInfoBlockValues}>
                    <p>{numberWithSeparator(airInfo?.supply)}</p>
                    <p>{numberWithSeparator(airInfo?.consume)}</p>
                    <p>{numberWithSeparator(floorRounding(airInfo?.count))}</p>
                    <p>{numberWithSeparator(airInfo?.purity)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className={styles.EmptyTableTitle}>No air info</p>
            )}
          </Grid>
          <Grid item xs={12} lg={9}>
            <Line options={options} data={data} />
            {!isAllHistory && (
              <div className={styles.PaginationButtonContainer}>
                <StyledButton
                  variant="contained"
                  onClick={() =>
                    setOffsetAirHistory(
                      offsetAirHistory < air.totalAirValues
                        ? offsetAirHistory + 50
                        : offsetAirHistory,
                    )
                  }
                  startIcon={<ArrowBackIosIcon />}
                  sx={{ margin: '16px' }}
                  disabled={offsetAirHistory >= air.totalAirValues - 50}
                  className={styles.PaginationButton}
                >
                  Previous
                </StyledButton>
                <StyledButton
                  variant="contained"
                  onClick={() =>
                    setOffsetAirHistory(offsetAirHistory ? offsetAirHistory - 50 : offsetAirHistory)
                  }
                  endIcon={<ArrowForwardIosIcon />}
                  sx={{ margin: '16px 0' }}
                  disabled={!offsetAirHistory}
                  className={styles.PaginationButton}
                >
                  Next
                </StyledButton>
              </div>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
});

export default AirQualityPage;
